// جلسات گروهی با ظرفیت (US-SCH-013؛ AP-CLN-03 — SCR-STAFF-302 «رزرو»): فهرست بازه با نشان «تکمیل»، تعریف جلسه (خدمت، ارائه‌دهنده، زمان، ظرفیت ۲ تا ۵۰)،
// ثبت‌نام با جستجوی سروری مراجعه‌کننده (نوبت جدا برای هر شرکت‌کننده؛ پس از تکمیل لیست انتظار جلسه با ارتقای خودکار)، خروج از لیست انتظار و لغو جلسه.
// فهرست شرکت‌کنندگان فقط نام و وضعیت نوبت است؛ هیچ داده بالینی در این صفحه نیست.
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BookingServiceOption, type SchGroupSessionView } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, DEFAULT_TIME_ZONE, faDigits, FormDialog, ListPage, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { formatJalaliInput, parseJalaliInput } from '../patients/common';
import { PatientPicker, type BookingPatient } from './BookingDialog';
import { addDays, AppointmentStatusChip, BranchRequired, formatDay, formatTime, useActiveBranch, useBookingOptions, useServiceSearch } from './common';

export const SCR_STAFF_302 = 'SCR-STAFF-302';
export const GROUP_SESSIONS_PATH = '/group-sessions';

const groupKeys = {
  list: (tenantId: string | undefined, branchId: string | null, from: string | null) => ['scheduling', tenantId, 'group-sessions', branchId, from] as const,
  detail: (tenantId: string | undefined, id: string) => ['scheduling', tenantId, 'group-session', id] as const,
};

/** زمان محلی شعبه (تاریخ کسب‌وکاری + ساعت) به لحظه UTC (ADR-014: تبدیل فقط در لبه). */
export function zonedInstant(businessDate: string, time: string, timeZone: string = DEFAULT_TIME_ZONE): string {
  const [year, month, day] = businessDate.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  const guess = Date.UTC(year!, month! - 1, day!, hour!, minute!);
  const parts = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(new Date(guess));
  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value);
  const asZoned = Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute'));
  return new Date(guess - (asZoned - guess)).toISOString();
}

function CreateDialog({ branchId, today, onClose, onSaved }: { branchId: string; today: string | null; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation('sch');
  const { tenantApi } = useTenantSession();
  const key = useIdempotencyKey();
  const serviceSearch = useServiceSearch(branchId, true);
  const [serviceId, setServiceId] = useState('');
  const [providerId, setProviderId] = useState('');
  const [date, setDate] = useState(formatJalaliInput(today ? addDays(today, 1) : null));
  const [time, setTime] = useState('10:00');
  const [capacity, setCapacity] = useState(6);
  const [title, setTitle] = useState('');
  const [touched, setTouched] = useState(false);
  const providers = useBookingOptions(branchId, serviceId || null, serviceId !== '').data?.providers ?? [];
  const businessDate = parseJalaliInput(date);
  const valid = serviceId !== '' && providerId !== '' && businessDate !== null && /^\d{2}:\d{2}$/.test(time) && capacity >= 2 && capacity <= 50;
  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/scheduling/group-sessions', {
          headers: { 'Idempotency-Key': key.current() },
          body: { branchId, serviceId, providerId, start: zonedInstant(businessDate!, time), capacity, roomId: null, title: title.trim() || null },
        }),
      ),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: onSaved,
  });
  return (
    <FormDialog
      open
      titleKey="sch:group.create"
      screenId={SCR_STAFF_302}
      onSubmit={() => {
        setTouched(true);
        if (valid) {
          save.mutate();
        }
      }}
      onClose={onClose}
      isDirty={serviceId !== '' || title !== ''}
      isSubmitting={save.isPending}
      submitError={save.error}
      maxWidth="md"
    >
      <TextField label={t('sch:group.title_label')} value={title} onChange={(event) => setTitle(event.target.value)} slotProps={{ htmlInput: { maxLength: 120, 'data-testid': 'group-title' } }} />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <Autocomplete<BookingServiceOption, false, false, false>
          value={serviceSearch.find(serviceId) ?? null}
          onChange={(_event, next) => {
            setServiceId(next?.id ?? '');
            setProviderId('');
          }}
          options={serviceSearch.services}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(items) => items}
          onInputChange={(_event, value, reason) => {
            if (reason === 'input') {
              serviceSearch.search(value);
            }
          }}
          loading={serviceSearch.isFetching}
          noOptionsText={t('sch:booking.service_none')}
          sx={{ flex: 2 }}
          renderInput={(params) => <TextField {...params} required label={t('sch:booking.service')} error={touched && serviceId === ''} inputProps={{ ...params.inputProps, 'data-testid': 'group-service' }} />}
        />
        <TextField select required label={t('sch:booking.provider')} value={providerId} error={touched && providerId === ''} onChange={(event) => setProviderId(event.target.value)} sx={{ flex: 1 }} slotProps={{ htmlInput: { 'data-testid': 'group-provider' } }}>
          {providers.map((provider) => (
            <MenuItem key={provider.id} value={provider.id}>
              {provider.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField required label={t('sch:group.date')} value={date} error={touched && businessDate === null} placeholder={t('sch:schedules.date_placeholder')} onChange={(event) => setDate(event.target.value)} slotProps={{ htmlInput: { dir: 'ltr', 'data-testid': 'group-date' } }} />
        <TextField required type="time" label={t('sch:group.time')} value={time} onChange={(event) => setTime(event.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { dir: 'ltr', step: 300, 'data-testid': 'group-time' } }} />
        <TextField
          required
          type="number"
          label={t('sch:group.capacity')}
          value={capacity}
          error={touched && (capacity < 2 || capacity > 50)}
          helperText={t('sch:group.capacity_hint')}
          onChange={(event) => setCapacity(Number(event.target.value) || 0)}
          slotProps={{ htmlInput: { min: 2, max: 50, 'data-testid': 'group-capacity' } }}
        />
      </Stack>
      <Alert severity="info">{t('sch:group.create_hint')}</Alert>
    </FormDialog>
  );
}

function DetailDialog({ sessionId, onClose }: { sessionId: string; onClose: () => void }) {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const key = useIdempotencyKey();
  const [patient, setPatient] = useState<BookingPatient | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const patientInput = useRef<HTMLInputElement>(null);
  const detail = useQuery({
    queryKey: groupKeys.detail(session?.tenantId, sessionId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/group-sessions/{id}', { params: { path: { id: sessionId } } })),
  });
  const refresh = (view: SchGroupSessionView) => {
    queryClient.setQueryData(groupKeys.detail(session?.tenantId, sessionId), view);
    void queryClient.invalidateQueries({ queryKey: ['scheduling', session?.tenantId, 'group-sessions'] });
  };
  const enroll = useMutation({
    mutationFn: () =>
      unwrap(tenantApi.POST('/api/v1/scheduling/group-sessions/{id}/participants', { params: { path: { id: sessionId } }, headers: { 'Idempotency-Key': key.current() }, body: { patientId: patient?.id ?? '' } })),
    onSettled: (_data, error) => key.settle(error ?? undefined),
    onSuccess: (result) => {
      setPatient(null);
      setNotice(t(result.outcome === 'Waitlisted' ? 'sch:group.waitlisted' : 'sch:group.enrolled'));
      refresh(result.session);
    },
  });
  const withdraw = useMutation({
    mutationFn: (participantId: string) => unwrap(tenantApi.POST('/api/v1/scheduling/group-sessions/{id}/participants/{participantId}:withdraw', { params: { path: { id: sessionId, participantId } } })),
    onSuccess: refresh,
  });
  const cancel = useMutation({
    mutationFn: () => unwrap(tenantApi.POST('/api/v1/scheduling/group-sessions/{id}:cancel', { params: { path: { id: sessionId } } })),
    onSuccess: refresh,
  });
  const data = detail.data;
  const open = data?.status === 'Scheduled';
  const canEnroll = open && access.can('sch.appointment.create');
  const canWithdraw = open && access.can('sch.waitlist.manage');
  const canCancel = open && access.can('sch.group_session.manage');
  return (
    <FormDialog open titleKey="sch:group.detail" screenId={SCR_STAFF_302} onSubmit={() => (patient && canEnroll ? enroll.mutate() : onClose())} onClose={onClose} isDirty={false} isSubmitting={enroll.isPending} submitError={enroll.error ?? withdraw.error ?? cancel.error} submitKey={canEnroll ? 'sch:group.enroll' : 'sch:group.close'} maxWidth="md">
      {data && (
        <Stack spacing={1.5} data-testid="group-detail" data-full={data.isFull}>
          <Typography variant="subtitle1">
            {data.title} · {formatDay(data.businessDate)} · <bdi dir="ltr">{formatTime(data.start)}</bdi>
          </Typography>
          <Stack direction="row" spacing={1}>
            <Chip label={t('sch:group.enrolled_of', { enrolled: faDigits(data.enrolled), capacity: faDigits(data.capacity) })} data-testid="group-enrolled" />
            {data.isFull && <Chip color="warning" label={t('sch:group.full')} data-testid="group-full" />}
            {Number(data.waitlisted) > 0 && <Chip variant="outlined" label={t('sch:group.waitlist_count', { count: faDigits(data.waitlisted) })} />}
            {data.status === 'Cancelled' && <StatusChip tone="cancelled" labelKey="sch:group.cancelled" />}
          </Stack>
          {notice && (
            <Alert severity="success" onClose={() => setNotice(null)} data-testid="group-notice">
              {notice}
            </Alert>
          )}
          {(data.participants ?? []).filter((participant) => participant.status !== 'Cancelled').map((participant) => (
            <Stack key={participant.id} direction="row" spacing={1} sx={{ alignItems: 'center' }} data-testid="group-participant" data-status={participant.status}>
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                {participant.patientName ?? '—'}
              </Typography>
              {participant.status === 'Waitlisted' ? (
                <Chip size="small" variant="outlined" label={t('sch:group.position', { position: faDigits(participant.position) })} />
              ) : participant.appointmentStatus ? (
                <AppointmentStatusChip status={participant.appointmentStatus} />
              ) : null}
              {participant.status === 'Waitlisted' && canWithdraw && (
                <Button size="small" color="inherit" onClick={() => withdraw.mutate(participant.id)} data-testid="group-withdraw">
                  {t('sch:group.withdraw')}
                </Button>
              )}
            </Stack>
          ))}
          {canEnroll && <PatientPicker value={patient} onChange={setPatient} disabled={enroll.isPending} inputRef={patientInput} />}
          {canCancel && Number(data.enrolled) === 0 && (
            <Button color="error" sx={{ alignSelf: 'flex-start' }} onClick={() => cancel.mutate()} data-testid="group-cancel">
              {t('sch:group.cancel')}
            </Button>
          )}
        </Stack>
      )}
    </FormDialog>
  );
}

export function GroupSessionsPage() {
  const { t } = useTranslation('sch');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const queryClient = useQueryClient();
  const { branchId } = useActiveBranch();
  const options = useBookingOptions(branchId);
  const today = options.data?.today ?? null;
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const list = useQuery({
    queryKey: groupKeys.list(session?.tenantId, branchId, today),
    enabled: branchId !== null && today !== null,
    queryFn: () => unwrap(tenantApi.GET('/api/v1/scheduling/group-sessions', { params: { query: { branchId: branchId ?? undefined, from: today!, to: addDays(today!, 30) } } })),
  });
  if (!branchId) {
    return <BranchRequired />;
  }
  const columns: DataTableColumn<SchGroupSessionView>[] = [
    { id: 'when', headerKey: 'sch:group.when', cell: (row) => <span>{formatDay(row.businessDate)} · <bdi dir="ltr">{formatTime(row.start)}</bdi></span> },
    { id: 'title', headerKey: 'sch:group.title_label', cell: (row) => row.title },
    { id: 'capacity', headerKey: 'sch:group.capacity', cell: (row) => t('sch:group.enrolled_of', { enrolled: faDigits(row.enrolled), capacity: faDigits(row.capacity) }) },
    { id: 'waitlist', headerKey: 'sch:group.waitlist', cell: (row) => faDigits(row.waitlisted) },
    {
      id: 'state',
      headerKey: 'sch:group.state',
      cell: (row) =>
        row.status === 'Cancelled' ? <StatusChip tone="cancelled" labelKey="sch:group.cancelled" /> : row.isFull ? <Chip size="small" color="warning" label={t('sch:group.full')} data-testid="group-row-full" /> : <Chip size="small" color="success" variant="outlined" label={t('sch:group.open')} />,
    },
    {
      id: 'open',
      headerKey: 'sch:group.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setSelected(row.id)} data-testid="group-open">
          {t('sch:group.open_detail')}
        </Button>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_302}
      titleKey="sch:group.page_title"
      subtitleKey="sch:group.page_subtitle"
      actions={
        access.can('sch.group_session.manage') ? (
          <Button variant="contained" onClick={() => setCreating(true)} data-testid="group-new">
            {t('sch:group.create')}
          </Button>
        ) : undefined
      }
    >
      <DataTable captionKey="sch:group.page_title" columns={columns} rows={list.data?.items} getRowId={(row) => row.id} isLoading={list.isPending} error={list.error} onRetry={() => void list.refetch()} emptyTitleKey="sch:group.empty" />
      {creating && (
        <CreateDialog
          branchId={branchId}
          today={today}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['scheduling', session?.tenantId, 'group-sessions'] });
          }}
        />
      )}
      {selected && <DetailDialog sessionId={selected} onClose={() => setSelected(null)} />}
    </ListPage>
  );
}
